import React from 'react';

import { Close, TranslateBadge } from '@travel/icons/ui';
import useWindowSizeChange from '@travel/traveler-core/components/NavigationTab/useWindowSizeChange';
import useDeviceType, { DESKTOP_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { Dialog } from '@travel/ui';
import { GalleryMedia } from '@travel/ui/components/Gallery';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import Swiper from 'components/Swiper';

import { Translate } from 'core/translate';

import styles from './photoGalleryDetail.module.scss';

type GalleryDetailProps = {
  isOpen: boolean;
  imageIndex?: number;
  mediaData: GalleryMedia[];
  onClose: () => void;
  onChangeIndex?: (index: number) => void;
};

function PhotoGalleryDetail({
  isOpen,
  onClose,
  imageIndex,
  mediaData,
  onChangeIndex,
}: GalleryDetailProps) {
  const isPC = useDeviceType() === DESKTOP_SCREEN;
  const { windowHeight } = useWindowSizeChange(0);

  const handleChange = (_: number, to: number) => {
    if (onChangeIndex) {
      onChangeIndex(to);
    }
  };

  return isOpen && isNotEmptyArray(mediaData) ? (
    // TODO TIDP-41160 Prevent bubbling to parent component
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      wrapperClassName={styles.gridDialog}
      className={styles.wrapper}
      hasEnableBack={false}
      isUseContentFlexBox={false}
      contentClassName={styles.detailDialogContent}
      overlayClassName={styles.dialogOverlay}
    >
      {isPC && (
        <button
          data-testid="back-button"
          type="button"
          className={styles.backButton}
          onClick={onClose}
          aria-label="back button"
        >
          <Close size={24} color="white" />
          <Translate id="Traveler_Common.Close" className={styles.title} />
        </button>
      )}

      <Swiper
        className={styles.swiperContent}
        bottomWrapperClassName={styles.bottomWrapper}
        containerClassName={styles.swipeContainer}
        navButtonWrapperClassName={styles.navButtonWrapper}
        navButtonClassName={styles.navButton}
        showNavButtons={isPC && mediaData.length > 1}
        initialSlide={imageIndex}
        shouldRenderAllChild={false}
        onChange={handleChange}
      >
        {mediaData.map((media, index) => (
          <div
            className={styles.mediaContainer}
            key={`${media.alt}-${index}`}
            data-testid={`photoGalleryDetail-wrapper-${index}`}
            style={{ height: windowHeight }}
          >
            <div className={styles.index}>
              {!isPC && (
                <button
                  data-testid="back-button"
                  type="button"
                  className={styles.backButton}
                  onClick={onClose}
                  aria-label="back button"
                >
                  <Close size={24} color="white" />
                </button>
              )}
              <span data-testid="photoGalleryDetail-index">
                {index + 1} / {mediaData.length}
              </span>
            </div>
            <Media
              wrapperClassName={cx(
                styles.mediaWrapper,
                media.type === 'video' ? styles.isVideo : null,
              )}
              className={cx(
                styles.mediaContent,
                media.type === 'video' ? styles.videoMediaContent : null,
              )}
              key={`${media.url}-${index}`}
              media={media}
              sizes="100vw"
              isPlayableVideo={true}
              isDisableLazyload={index === imageIndex}
            />
            <div
              className={cx(
                styles.textWrapper,
                !media?.name && !media.description ? styles.emptyText : null,
              )}
            >
              {media?.name && <p data-testid="photoGalleryDetail-title">{media?.name}</p>}
              {media?.description && (
                <p className={styles.description} data-testid="photoGalleryDetail-description">
                  {media?.description}
                </p>
              )}
              {(media?.name || media?.description) && media.isTextsGoogleTranslated ? (
                <div className={styles.translate} data-testid="photoGalleryDetail-translateBadge">
                  <TranslateBadge color="white" />
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </Swiper>
    </Dialog>
  ) : null;
}

export default PhotoGalleryDetail;
